<template>
  <nav v-if="store.token" class="nav-container">
    <!-- Desktop Menu -->
    <div class="desktop-menu">
      <router-link 
        v-for="item in filteredItems" 
        :key="item.label"
        :to="item.to || ''"
        class="nav-item"
        :class="{ 'has-submenu': item.items }"
        @click="item.command ? item.command() : null"
      >
        <i :class="item.icon"></i>
        <span>{{ item.label }}</span>
        <div v-if="item.items" class="submenu">
          <router-link 
            v-for="subItem in item.items" 
            :key="subItem.label"
            :to="subItem.to || ''"
            class="submenu-item"
            @click="subItem.command ? subItem.command() : null"
          >
            <i :class="subItem.icon"></i>
            <span>{{ subItem.label }}</span>
          </router-link>
        </div>
      </router-link>
    </div>

    <!-- Mobile Menu -->
    <div class="mobile-menu">
      <Button
        class="menu-toggle"
        icon="pi pi-bars"
        @click="isMobileMenuOpen = !isMobileMenuOpen"
      />
      <div v-if="isMobileMenuOpen" class="mobile-menu-items">
        <router-link 
          v-for="item in allChildMenus"
          :key="item.label"
          :to="item.to || ''"
          class="mobile-nav-item"
          @click="onNavButtonClick(item)"
        >
          <i :class="item.icon"></i>
          <span>{{ item.label }}</span>
        </router-link>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { usePlugin } from "@/plugins";
import { computed, defineComponent, ref } from "vue";

interface MenuItem {
  label: string;
  icon: string;
  to?: { name: string };
  command?: () => void;
  items?: MenuItem[];
}

export default defineComponent({
  name: "Navigation",
  setup() {
    const { store, router, confirm, axios } = usePlugin();
    const isMobileMenuOpen = ref(false);

    const items = ref<MenuItem[]>([
      { label: "Home", icon: "pi pi-fw pi-home", to: { name: "Home" } },
      {
        label: "Shipment Report",
        icon: "pi pi-fw pi-check-square",
        to: { name: "ShipmentCheck" },
      },
      {
        label: "Performance Report",
        icon: "pi pi-fw pi-chart-bar",
        to: { name: "PerformanceSummaryReport" },
      },
      {
        label: "ปรับแต่งระบบ",
        icon: "pi pi-fw pi-cog",
        items: [
          {
            label: "ผู้ใช้งาน",
            to: { name: "UserList" },
            icon: "pi pi-fw pi-user",
          },
        ],
      },
      {
        label: "ออกจากระบบ",
        icon: "pi pi-fw pi-unlock",
        command: () => onLogout(),
      },
    ]);

    const filteredItems = computed(() => {
      let menu = items.value;
      if (!store.value.adminAuthority) {
        menu = menu.filter((x) => x.label !== "ปรับแต่งระบบ");
      }
      if (!store.value.reportAuthority || !store.value.salesOfficeAuthority) {
        menu = menu.filter((x) => x.label.indexOf("Report") < 0);
      }
      return menu;
    });

    const allChildMenus = computed(() => {
      const result: any[] = [];
      for (const item of filteredItems.value) {
        result.push(...extractChilds(item));
      }
      return result;
    });

    const extractChilds = (x: any) => {
      const result: any[] = [];
      if (x.items && x.items.length > 0) {
        for (const child of x.items) {
          result.push(...extractChilds(child));
        }
      } else {
        result.push(x);
      }
      return result;
    };

    const onNavButtonClick = (item: any) => {
      if (item.to) {
        router.push(item.to);
      } else if (item.command) {
        item.command();
      }
      isMobileMenuOpen.value = false;
    };

    const onLogout = () => {
      confirm.require({
        header: "ออกจากระบบ",
        message: "ยืนยันออกจากระบบ?",
        type: "danger",
        acceptClass: "p-button-danger",
        accept: () => {
          store.value.token = "";
          store.value.username = "";
          store.value.name = "";
          store.value.salesOfficeAuthority = "";
          store.value.adminAuthority = false;
          store.value.reportAuthority = false;

          router.replace({ name: "Login" });
          axios.defaults.headers = {
            Accept: "application/json, text/plain, */*",
          };
        },
      });
    };

    return {
      store,
      onLogout,
      items,
      allChildMenus,
      onNavButtonClick,
      filteredItems,
      isMobileMenuOpen,
    };
  },
});
</script>

<style scoped>
.nav-container {
  background: var(--surface-0);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  padding: 0.5rem;
}

.desktop-menu {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  text-decoration: none;
  color: var(--text-color);
  border-radius: 4px;
  position: relative;
}

.nav-item:hover {
  background: var(--surface-100);
}

.has-submenu {
  position: relative;
}

.submenu {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  background: var(--surface-0);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-radius: 4px;
  min-width: 200px;
  z-index: 1000;
}

.has-submenu:hover .submenu {
  display: block;
}

.submenu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  text-decoration: none;
  color: var(--text-color);
}

.submenu-item:hover {
  background: var(--surface-100);
}

.mobile-menu {
  display: none;
}

.mobile-menu-items {
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  background: var(--surface-0);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  padding: 0.5rem;
  z-index: 1000;
}

.mobile-nav-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: var(--text-color);
  border-bottom: 1px solid var(--surface-200);
}

.mobile-nav-item:last-child {
  border-bottom: none;
}

.mobile-nav-item:hover {
  background: var(--surface-100);
}

@media (max-width: 991px) {
  .desktop-menu {
    display: none;
  }
  
  .mobile-menu {
    display: block;
  }
}
</style>
